/**
 * Benefit — the core benefit/plan entity.
 *
 * Includes both the DynamoDB schema (`DynamoBenefit`) and the
 * domain-level interface (`Benefit`) used by the frontend.
 *
 * Almost every field is optional on the backend.
 *
 * @todo Replace with source of truth from `@enrollment-platform-service` package.
 */

import type { CarrierResponseStatus } from './carrierResponse'

/* -------------------------------------------------------------------------- */
/*                                   Enums                                    */
/* -------------------------------------------------------------------------- */

export enum ApplicationStatus {
  WORKER_SUBMITTED = 'Worker Submitted',
  HIXME_SUBMITTED = 'Hixme Submitted',
  VALIDATED = 'Validated',
}

/** User-facing labels for carrier application status (API values stay unchanged). */
export const CARRIER_APPLICATION_STATUS_LABELS: Record<
  ApplicationStatus,
  string
> = {
  [ApplicationStatus.WORKER_SUBMITTED]: 'Worker Submitted',
  [ApplicationStatus.HIXME_SUBMITTED]: 'SureCo Submitted',
  [ApplicationStatus.VALIDATED]: 'Verified',
}

export const CARRIER_APPLICATION_STATUS_OPTIONS = Object.values(
  ApplicationStatus
).map((value) => ({
  value,
  label: CARRIER_APPLICATION_STATUS_LABELS[value],
}))

export function getCarrierApplicationStatusLabel(
  status?: string | null
): string {
  if (!status) return '-'
  return (
    CARRIER_APPLICATION_STATUS_LABELS[status as ApplicationStatus] ?? status
  )
}

export enum CarrierApplicationEscalationType {
  NEEDS_ATTENTION = 'Needs Attention',
  NEEDS_QLE_DOCUMENT = 'Needs QLE Document',
  NEEDS_BINDER_PAYMENT = 'Needs Binder Payment',
}

export enum CarrierPlanChangeIndicator {
  SAME_CARRIER = 'SAME CARRIER',
  CHANGED_DEMOGRAPHICS = 'CHANGED DEMOGRAPHICS',
  CHANGED_CARRIER = 'CHANGED CARRIER',
  CHANGED_PLAN = 'CHANGED PLAN',
  NEW_APPLICATION = 'NEW APPLICATION',
}

export enum BenefitTypeEnum {
  HEALTH_BUNDLE = 'HealthBundle',
  ICHRA = 'ICHRA',
  ACCIDENT = 'Accident',
  CHIROPRACTIC = 'Chiropractic',
  DENTAL_INSURANCE = 'DentalInsurance',
  DEPENDENT_CARE_ACCOUNT = 'DependentCareAccount',
  EMPLOYEE_ASSISTANCE_PROGRAM = 'EmployeeAssistanceProgram',
  EMPLOYEE_INDICATED_INTEREST = 'EmployeeIndicatedInterest',
  FLEXIBLE_SPENDING_ACCOUNT = 'FlexibleSpendingAccount',
  FLEXIBLE_TRANSIT_ACCOUNT = 'FlexibleTransitAccount',
  GROUP_HEALTH = 'GroupHealth',
  HEALTH_SAVINGS_ACCOUNT = 'HealthSavingsAccount',
  HOSPITAL_INDEMNITY = 'HospitalIndemnity',
  LEGAL_PLAN = 'LegalPlan',
  LEGAL_SHIELD = 'LegalShield',
  LIMITED_PURPOSE_FSA = 'LimitedPurposeFlexibleSpendingAccount',
  LONG_TERM_DISABILITY = 'LongTermDisability',
  SHORT_TERM_DISABILITY = 'ShortTermDisability',
  SIMPLE_VOLUNTARY_LIFE = 'SimpleVoluntaryLifeInsurance',
  SUPPLEMENTAL_LIFE = 'SupplementalLifeInsurance',
  VISION_INSURANCE = 'VisionInsurance',
  VOLUNTARY_ADD = 'VoluntaryADD',
  VOLUNTARY_LIFE = 'VoluntaryLifeInsurance',
  VOLUNTARY_LTD = 'VoluntaryLongTermDisability',
  VOLUNTARY_STD = 'VoluntaryShortTermDisability',
}

export type BenefitPopulateOption =
  | 'persons'
  | 'enrollment'
  | 'enrollment:slim'
  | 'client'

export enum PaymentTypeEnum {
  VCN = 'VCN',
  ACH = 'ACH',
  CHECK = 'Check',
  GROUP_ACH = 'GROUP ACH',
}

/** Primary payment method for carrier application (ACH, VCN, CHECK, or GROUP ACH). */
export type PrimaryPaymentMethod =
  | PaymentTypeEnum.ACH
  | PaymentTypeEnum.VCN
  | PaymentTypeEnum.CHECK
  | PaymentTypeEnum.GROUP_ACH

/** Binder payment method (carrier application) — VCN, ACH, or Check. */
export type BinderPaymentMethod = PaymentTypeEnum

/* -------------------------------------------------------------------------- */
/*                          Carrier Application History                       */
/* -------------------------------------------------------------------------- */

interface CarrierApplicationStatusHistory {
  status: ApplicationStatus
  statusDate: Date
  updatedBy: string
}

/* -------------------------------------------------------------------------- */
/*                             Benefit Interface                              */
/* -------------------------------------------------------------------------- */

export interface Benefit {
  /* ---------------------------------- Core ---------------------------------- */
  id: string
  benefitPublicKey?: string
  employeeBenefitPublicKey?: string
  enrollmentPublicKey?: string
  clientPublicKey?: string
  employeePublicKey?: string
  employeeName?: string
  /**
   * Insured person label for Application Engine UI (one row per application).
   * Enriched only on GET .../application-engine and .../application-engine/export.
   */
  displayName?: string
  /**
   * Relationship of the insured person on this application (e.g. Employee, Child).
   * Enriched on Application Engine routes only — same source as displayName.
   */
  relationship?: string
  companyName?: string
  benefitType?: string
  description?: string
  name?: string
  summary?: string

  /* --------------------------------- Dates ---------------------------------- */
  benefitEffectiveDate?: Date
  benefitEndDate?: Date
  benefitEndDateOld?: Date
  benefitEndDateBad?: Date
  createdAt?: string
  updatedAt?: string
  cartCreatedDate?: Date

  /* -------------------------------- Pricing --------------------------------- */
  price?: string
  fullPrice?: string
  cobraPrice?: string
  hsaPrice?: string
  monthlyDeduction?: string
  isPriceDefined?: boolean

  /* ------------------------------- Plan Info -------------------------------- */
  planName?: string
  planType?: string
  planNumber?: string
  planPolicyNumber?: string
  policyNumber?: string
  healthPlanId?: string
  metalLevel?: string
  actuarialValue?: string
  serviceAreaId?: string
  exchange?: string
  planNetworks?: string
  starRatings?: string

  /* -------------------------------- Carrier --------------------------------- */
  carrier?: string
  carrierName?: string
  carrierWebsite?: string
  carrierCsn?: string
  carrierApplication?: string
  carrierApplicationWorkerSubmittedDate?: Date
  carrierApplicationValidatedBy?: string
  carrierApplicationValidatedDate?: Date
  carrierApplicationStatus?: string
  carrierApplicationApplicationId?: string
  carrierApplicationHixmeSubmittedBy?: string
  carrierApplicationHixmeSubmittedDate?: Date
  carrierApplicationConfirmedCarrierPlanCost?: number
  carrierApplicationStatusHistory?: CarrierApplicationStatusHistory[]
  carrierApplicationValidatedNotify?: boolean
  carrierApplicationBinderPaymentAutopayEnabled?: boolean
  carrierApplicationBinderPaymentType?: BinderPaymentMethod
  carrierApplicationPrimaryPaymentMethod?: PrimaryPaymentMethod
  carrierApplicationPrimaryACHAccountNumber?: string
  carrierApplicationPrimaryACHAccountType?: string
  carrierApplicationPrimaryACHAccountUse?: string
  carrierApplicationNeedsAttention?: boolean
  carrierApplicationNeedsQLEDocument?: boolean
  carrierApplicationNeedsBinderPayment?: boolean
  carrierApplicationCarrierResponse?: CarrierResponseStatus | null
  carrierApplicationSubmittingAgent?: 'MC' | 'MK'
  carrierApplicationPaymentConfirmation?: string
  carrierApplicationNotes?: string
  carrierApplicationNoteCreator?: string
  carrierApplicationNotesCreated?: Date
  carrierPlanChange?: string
  assignee?: string

  /* ------------------------------ Benefit Math ------------------------------ */
  math?: string
  hsamath?: string
  benefitAdd?: number
  maxBenefitAdd?: number
  minBenefit?: number
  benefitLife?: number
  maxBenefitLife?: number
  hixmeValue?: number
  experienceRatingAdjustment?: number
  hraadminFee?: number

  /* --------------------------------- Cart ----------------------------------- */
  cartCreatedBy?: string
  cartCreatedByPersonPublicKey?: string
  autoAddToCart?: boolean
  canElect?: string
  canDecline?: boolean
  canRemoveFromCart?: boolean

  /* -------------------------------- Coverage -------------------------------- */
  coverageTier?: string
  coinsurance?: string
  deductible?: string
  copayCap?: boolean
  coveredHospitals?: string
  coveredDoctors?: string
  providers?: string
  primaryCareProvider?: string
  pcp?: string
  preventativeServices?: string
  basicServices?: string
  majorServices?: string
  officeVisit?: string
  faceToFaceVisits?: string
  phoneAssistance?: string
  maxAnnualVisits?: string

  /* ---------------------------------- HSA ----------------------------------- */
  isHsa?: boolean
  hsaswap?: boolean
  hsapartialBundle?: boolean
  singleMax?: number
  marriedFilingJointlyMax?: number
  marriedFilingSeperateMax?: number

  /* --------------------------------- Bundle --------------------------------- */
  bundlePublicKey?: string
  bundleBenefits?: string
  bundleHsabenefits?: string
  bundleLevel?: string
  lowestNonHsaBundle?: boolean
  recommendedPpoBundle?: boolean
  recommendedHmoBundle?: boolean
  recommendedHsaBundle?: boolean
  recommendedBundle?: boolean

  /* ------------------------------ Hixme Products ---------------------------- */
  hixmePlus?: boolean
  hixmePlusCoverage?: number
  hixmeAccident?: boolean
  hixmeAccidentGroupId?: string
  hixmeHospitalPlusCoverage?: number
  hospitalPlus?: number
  hixmeCopayCapUrl?: string
  hasAccident?: boolean
  hasTransConnect?: boolean

  /* -------------------------------- Dental ---------------------------------- */
  orthodontic?: string
  orthodonticLifetimeBenefitMaximum?: string
  annualBenefitMaximum?: string

  /* --------------------------------- Vision --------------------------------- */
  eyeExams?: string
  lenses?: string
  frames?: string
  contacts?: string

  /* ----------------------------- Life / Disability -------------------------- */
  guaranteeIssueAmmount?: number
  benefitDuration?: string
  eliminationPeriodDays?: string
  benefitPercentageOfMonthlyEarnings?: string
  maxMonthlyBenefit?: number
  monthylyBenefitCap?: number
  maxWeeklyBenefit?: number
  maxAnnualCoveredIncome?: string

  /* ------------------------------- Voluntary -------------------------------- */
  spouseCanElect?: boolean
  spouseMaxAmount?: number
  spouseMinAmount?: number
  spouseIncrementAmount?: number
  spouseElection?: number
  employeeElection?: number
  employeeMinAmount?: number
  employeeMaxAmount?: number
  employeeMinAge?: number
  employeeMaxAge?: number
  employeeIncrementAmount?: number
  employeeContributionAmount?: number
  employeeContributionMax?: number
  rolloverAmount?: number

  /* -------------------------------- COBRA ----------------------------------- */
  cobra?: string
  hsacobra?: string

  /* -------------------------------- Status ---------------------------------- */
  isActive?: boolean
  isTestUser?: boolean
  isSet?: boolean
  adminFee?: boolean

  /* ---------------------------------- SBC ----------------------------------- */
  sbcurl?: string
  sbcLinks?: string
  bc1Url?: string
  bc2Benefit?: number

  /* ------------------------------ Documents --------------------------------- */
  envelopeComplete?: boolean
  docuSignEnvelopeId?: string
  pdfApplicationAvailable?: boolean
  pdfSignatures?: string
  healthInsuranceApplications?: string
  unsignedPdfApplication?: string
  documentLocation?: string
  doctorInstructionsUrl?: string

  /* -------------------------------- Digital Mail ----------------------------- */
  digitalMailConfig?: string

  /* -------------------------------- Virtual Card ----------------------------- */
  virtualCardId?: string
  vcnstatus?: string
  virtualCardKey?: string
  vcnProvider?: string

  /* -------------------------------- Billing --------------------------------- */
  billingId?: string

  /* ---------------------------------- CHAD ---------------------------------- */
  chadAdded?: boolean
  chadAddedDate?: Date

  /* ------------------------------- Messaging -------------------------------- */
  benefitAddedMessage?: string
  benefitDeclinedMessage?: string
  note?: string
  tooltip?: string

  /* ------------------------------- Misc / Other ----------------------------- */
  persons?: string
  benefitChangeActions?: string
  benefitOptions?: string
  benefitSet?: string
  eligibility?: string
  benefit?: string
  sections?: any
  components?: string
  groupNumber?: string
  proofOfResidenceRequired?: boolean
  annualTransitionGracePeriodDays?: number
  standardGracePeriodDays?: number
  reimbursementAllowance?: string
  updatedBy?: string
  updatedByPersonPublicKey?: string
  isTerminated?: boolean
  terminationReason?: Record<string, string | boolean>

  /* ------------------------------ Math / Pricing ----------------------------- */
  /** Calculated pricing (from Math.TotalPrice, etc.) */
  mathTotalPrice?: number
  mathWorkerPrice?: number
  mathEmployerPrice?: number
  mathBundleTotalPrice?: number
  mathBundleWorkerPrice?: number
  mathBundleEmployerPrice?: number

  /* ------------------------------ Hevo Metadata ----------------------------- */
  __hevo__ingested_at?: bigint
  __hevo__marked_deleted?: boolean
  __hevo__source_modified_at?: bigint
}

/* -------------------------------------------------------------------------- */
/*                       DynamoBenefit (DynamoDB Schema)                      */
/* -------------------------------------------------------------------------- */

export interface DynamoBenefitKey {
  Id: string
}

export interface DynamoCarrierApplication {
  Status: string
  ApplicationId?: string
  HixmeSubmittedBy?: string
  HixmeSubmittedDate?: string
  WorkerSubmittedDate?: string
  ValidatedDate?: string
  ValidatedBy?: string
  ValidatedNotify?: boolean
  ConfirmedCarrierPlanCost?: number
  StatusHistory?: {
    Status: string
    StatusDate: string
    UpdatedBy?: string
  }
}

export interface DynamoBenefit extends DynamoBenefitKey {
  BenefitEffectiveDate: string
  BenefitEndDate: string
  CarrierName: string
  ClientPublicKey: string
  EmployeePublicKey: string
  EnrollmentPublicKey: string
  CarrierApplication: DynamoCarrierApplication
  Assignee?: string
  CarrierPlanChange?: string
  Persons?: unknown[]
  HealthPlanId?: string
  BenefitType?: string
  IsActive?: boolean
  EmployeeName?: string
  CompanyName?: string
  PolicyNumber?: string
  PlanPolicyNumber?: string
  IsTestUser?: boolean
  PlanName?: string
}

export const BULK_UPDATE_MAX_LIMIT = 300
export const APPLICATION_ENGINE_PAGE_SIZE = 50
export const APPLICATION_ENGINE_PAGE_SIZE_OPTIONS = [50, 100]
